import { z } from 'zod';
import { pageSchema, resourceKeySchema, timestampSchema } from './common';

/**
 * Automations (architecture documentation sections 8.3 and 26, phase 4).
 *
 * An automation is declarative configuration -- a trigger, a set of conditions
 * and a list of actions, all drawn from closed vocabularies. It is deliberately
 * not a scripting surface. Section 8.3 says arbitrary runtime execution needs a
 * designed sandbox, compatibility story and operational owner, and none of that
 * is worth building to let an operator say "when this service degrades, mark
 * that project at risk".
 *
 * The consequence is that the blast radius is bounded by construction: an
 * automation can only do the things enumerated in `automationActionSchema`, and
 * every one of them is audited.
 */

/* -------------------------------------------------------------------------- */
/* Triggers                                                                    */
/* -------------------------------------------------------------------------- */

export const AUTOMATION_EVENTS = [
  'service.status_changed',
  'service.degraded',
  'service.recovered',
  'job.dead_lettered',
  'integration.failed',
  'webhook.received',
] as const;
export const automationEventSchema = z.enum(AUTOMATION_EVENTS);
export type AutomationEvent = (typeof AUTOMATION_EVENTS)[number];

export const automationTriggerSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('schedule'),
    /**
     * An interval, not a cron expression. Cron invites "every Tuesday at
     * 03:17" precision that this system cannot honour anyway -- the worker
     * polls, so the real resolution is the poll interval, and an interval makes
     * that honest.
     */
    intervalSeconds: z.number().int().min(60).max(86_400),
  }),
  z.object({ type: z.literal('event'), event: automationEventSchema }),
]);
export type AutomationTrigger = z.infer<typeof automationTriggerSchema>;

/* -------------------------------------------------------------------------- */
/* Conditions                                                                  */
/* -------------------------------------------------------------------------- */

export const conditionOperatorSchema = z.enum([
  'equals',
  'not_equals',
  'in',
  'not_in',
  'greater_than',
  'less_than',
  'contains',
  'exists',
]);

/**
 * Conditions read a named field from the trigger payload, not an arbitrary
 * expression. `path` is a dotted key into a shape the engine controls, so a
 * condition cannot reach into anything the event did not deliberately expose.
 */
export const automationConditionSchema = z
  .object({
    path: z
      .string()
      .min(1)
      .max(120)
      .regex(/^[a-zA-Z][a-zA-Z0-9_]*(\.[a-zA-Z][a-zA-Z0-9_]*)*$/, 'must be a dotted field path'),
    operator: conditionOperatorSchema,
    value: z
      .union([z.string().max(500), z.number(), z.boolean(), z.array(z.string().max(200)).max(50)])
      .optional(),
  })
  .strict();
export type AutomationCondition = z.infer<typeof automationConditionSchema>;

/* -------------------------------------------------------------------------- */
/* Actions                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * The closed set of things an automation may do.
 *
 * Notably absent: enqueueing arbitrary jobs, calling arbitrary URLs, and
 * modifying users or roles. Each of those would turn automation configuration
 * into a privilege-escalation path -- an editor who can write an automation
 * would gain whatever the automation can reach.
 */
export const automationActionSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('project.set_health'),
    projectId: z.string().uuid(),
    health: z.enum(['on_track', 'at_risk', 'off_track', 'unknown']),
  }),
  z.object({
    type: z.literal('integration.pause'),
    integrationId: z.string().uuid(),
  }),
  z.object({
    type: z.literal('annotation.create'),
    severity: z.enum(['info', 'warning', 'critical']),
    /** Supports {{field}} interpolation from the trigger payload. */
    message: z.string().min(1).max(500),
  }),
]);
export type AutomationAction = z.infer<typeof automationActionSchema>;

/* -------------------------------------------------------------------------- */
/* Definition                                                                  */
/* -------------------------------------------------------------------------- */

export const automationDefinitionSchema = z
  .object({
    trigger: automationTriggerSchema,
    conditions: z.array(automationConditionSchema).max(10).default([]),
    /**
     * Bounded so one automation cannot fan out into a long chain of writes, and
     * so a run has a predictable cost.
     */
    actions: z.array(automationActionSchema).min(1).max(5),
    /**
     * Refuses to fire again within this window. An event-driven automation on a
     * flapping service would otherwise run continuously and bury the signal it
     * exists to surface.
     */
    cooldownSeconds: z.number().int().min(0).max(86_400).default(300),
  })
  .strict();
export type AutomationDefinition = z.infer<typeof automationDefinitionSchema>;

export const automationSchema = z.object({
  id: z.string().uuid(),
  key: resourceKeySchema,
  name: z.string(),
  description: z.string().nullable(),
  enabled: z.boolean(),
  definition: automationDefinitionSchema,
  lastRunAt: timestampSchema.nullable(),
  lastRunStatus: z.enum(['succeeded', 'skipped', 'failed']).nullable(),
  runCount: z.number().int().nonnegative(),
  version: z.number().int().nonnegative(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type Automation = z.infer<typeof automationSchema>;

export const automationPageSchema = pageSchema(automationSchema);

export const createAutomationSchema = z
  .object({
    key: resourceKeySchema,
    name: z.string().min(1).max(200),
    description: z.string().max(2000).nullish(),
    /** New automations start disabled, so a mistake is not live on save. */
    enabled: z.boolean().default(false),
    definition: automationDefinitionSchema,
  })
  .strict();
export type CreateAutomation = z.infer<typeof createAutomationSchema>;

export const updateAutomationSchema = z
  .object({
    name: z.string().min(1).max(200).optional(),
    description: z.string().max(2000).nullish(),
    enabled: z.boolean().optional(),
    definition: automationDefinitionSchema.optional(),
    version: z.number().int().nonnegative(),
  })
  .strict();
export type UpdateAutomation = z.infer<typeof updateAutomationSchema>;

export const automationListQuerySchema = z.object({
  enabled: z.coerce.boolean().optional(),
  cursor: z.string().max(256).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
});

/* -------------------------------------------------------------------------- */
/* Execution history                                                           */
/* -------------------------------------------------------------------------- */

export const automationRunStatusSchema = z.enum(['succeeded', 'skipped', 'failed']);

/** One line per action attempted, so a run explains itself without a debugger. */
export const automationRunStepSchema = z.object({
  action: z.string(),
  outcome: z.enum(['applied', 'skipped', 'failed']),
  detail: z.string().nullable(),
});

export const automationRunSchema = z.object({
  id: z.string().uuid(),
  automationId: z.string().uuid(),
  automationKey: z.string(),
  status: automationRunStatusSchema,
  /** What set it off: the event name, `schedule`, or a user id for a manual run. */
  triggeredBy: z.string(),
  startedAt: timestampSchema,
  completedAt: timestampSchema.nullable(),
  durationMs: z.number().int().nonnegative().nullable(),
  /** Present when conditions did not match, explaining which one failed. */
  skipReason: z.string().nullable(),
  steps: z.array(automationRunStepSchema),
  errorCode: z.string().nullable(),
});
export type AutomationRun = z.infer<typeof automationRunSchema>;

export const automationRunPageSchema = pageSchema(automationRunSchema);

/**
 * Evaluates one condition against a flattened trigger payload.
 *
 * Kept in the contracts package so the engine and the frontend preview agree
 * exactly -- an operator testing a condition in the browser must get the same
 * answer the worker will.
 */
export function evaluateCondition(
  condition: AutomationCondition,
  payload: Record<string, unknown>,
): boolean {
  const actual = readPath(payload, condition.path);

  switch (condition.operator) {
    case 'exists':
      return actual !== undefined && actual !== null;
    case 'equals':
      return String(actual) === String(condition.value);
    case 'not_equals':
      return String(actual) !== String(condition.value);
    case 'in':
      return Array.isArray(condition.value) && condition.value.includes(String(actual));
    case 'not_in':
      return Array.isArray(condition.value) && !condition.value.includes(String(actual));
    case 'greater_than':
      return typeof actual === 'number' && typeof condition.value === 'number'
        ? actual > condition.value
        : false;
    case 'less_than':
      return typeof actual === 'number' && typeof condition.value === 'number'
        ? actual < condition.value
        : false;
    case 'contains':
      return typeof actual === 'string' && String(actual).includes(String(condition.value));
  }
}

function readPath(payload: Record<string, unknown>, path: string): unknown {
  let current: unknown = payload;
  for (const segment of path.split('.')) {
    if (typeof current !== 'object' || current === null) return undefined;
    // Own properties only: a path of `constructor.prototype` must not walk the
    // prototype chain into anything the payload did not define.
    if (!Object.prototype.hasOwnProperty.call(current, segment)) return undefined;
    current = (current as Record<string, unknown>)[segment];
  }
  return current;
}

/** Interpolates `{{field}}` placeholders in an annotation message. */
export function interpolate(template: string, payload: Record<string, unknown>): string {
  return template.replace(/\{\{\s*([a-zA-Z][a-zA-Z0-9_.]*)\s*\}\}/g, (match, path: string) => {
    const value = readPath(payload, path);
    if (value === undefined || value === null) return match;

    /*
     * Only scalars interpolate, and the allowed set is stated positively.
     *
     * `payload` is whatever the triggering event carried, so a placeholder can
     * name anything at all -- and `String()` on an object gives
     * "[object Object]", on an array a comma-joined mess, on a symbol a throw.
     * Every one of those would be rendered into an annotation an operator is
     * expected to act on. Leaving the placeholder intact says "this field is
     * not a value" far more clearly than a stringified object does.
     */
    if (typeof value === 'string') return value.slice(0, 200);
    if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
      return String(value);
    }
    return match;
  });
}
